import React, { forwardRef, useEffect, useState } from 'react'
import { Col, Form, Grid, Row } from 'rsuite'
import FormTextField from './FormTextField'
import UnrestrictedAddressLookup from './UnrestrictedAddressLookup'
import CityRestrictedAddressLookup from './CityRestrictedAddressLookup'
import { FORM_INPUT_TYPES } from '../../common/enums'
import { convertSnakeCaseToCamelCase, isNullUndefinedOrEmptyObject, objectHasProperty, titleize } from '../../common/utils'
import AnnotatedCheckbox from './AnnotatedCheckbox'

const PersonDetails = forwardRef(
    (
        {
            formModel,
            formError,
            validationModel,
            setFormError,
            setFormValues,
            formValues,
            restrictAddressToCity = false,
            emailDisabled = false,
            firstNameDisabled = false,
            lastNameDisabled = false,
            showEmail = true,
            showSameAsPropertyAddressCheckboxOption = false,
            onSameAsPropertyAddressChanged = () => {},
            onUnrestrictedAddressLookupChange = () => { },
            optionalFields
        },
        ref
    ) => {
        const [parsedOptionalFields, setParsedOptionalFields] = useState({});
        useEffect(() => {
            if (isNullUndefinedOrEmptyObject(optionalFields)) return;
            let parsedFields = {};
            Object.keys(optionalFields).forEach(key => {
                let parsedKey = convertSnakeCaseToCamelCase(key);
                parsedFields[parsedKey] = optionalFields[key];
            });
            setParsedOptionalFields(parsedFields);
        }, [optionalFields]);

        const onAddressTextChanged = () => {
            onSameAsPropertyAddressChanged(false);
        }

        return (
            <Form
                model={formModel}
                ref={ref}
                checkTrigger="none"
                formDefaultValue={formValues}
                formValue={formValues}
                onCheck={(e) => {
                    setFormError(e);
                }}
                onChange={(values) => {
                    setFormValues({
                        ...formValues,
                        ...values
                    })
                }}
                fluid
            >
                <Grid fluid>
                    <Row gutter={24} className="mb-12">
                        <Col xs={24} md={12} className='sm-pb-12'>
                            <FormTextField
                                name="firstName"
                                label={`First Name${objectHasProperty("firstName", parsedOptionalFields) && parsedOptionalFields["firstName"] === "optional" ? " (optional)" : ""}`}
                                hasError={formError['firstName']}
                                validationRule={validationModel.firstName}
                                errorMessage={formError['firstName']}
                                disabled={firstNameDisabled}
                                value={formValues?.firstName ?? ""}
                            />
                        </Col>
                        <Col xs={24} md={12}>
                            <FormTextField
                                name="lastName"
                                label={`Last Name${objectHasProperty("lastName", parsedOptionalFields) && parsedOptionalFields["lastName"] === "optional" ? " (optional)" : ""}`}
                                hasError={formError['lastName']}
                                validationRule={validationModel.lastName}
                                errorMessage={formError['lastName']}
                                disabled={lastNameDisabled}
                                value={formValues?.lastName ?? ""}
                            />
                        </Col>
                    </Row>
                    <Row gutter={24} className="mb-12">
                        <Col xs={24}>
                            <FormTextField
                                name="companyName"
                                label="Company Name (optional)"
                                hasError={formError["companyName"]}
                                validationRule={validationModel.companyName}
                                value={formValues?.companyName ?? ""}
                                errorMessage={formError["companyName"]}
                            />
                        </Col>
                    </Row>
                    {
                        showEmail && (
                            <Row gutter={24} className="mb-12">
                                <Col xs={24}>
                                    <FormTextField
                                        name="email"
                                        label={`Email Address${objectHasProperty("email", parsedOptionalFields) && parsedOptionalFields["email"] === "optional" ? " (optional)" : ""}`}
                                        hasError={formError['email']}
                                        validationRule={validationModel.email}
                                        errorMessage={formError['email']}
                                        disabled={emailDisabled}
                                        value={formValues?.email ?? ""}
                                        inputFieldType='email'
                                    />
                                </Col>
                            </Row>
                        )
                    }
                    <Row gutter={24} className="mb-12">
                        <Col xs={24}>
                            <FormTextField
                                name="phone"
                                label={`Phone Number${objectHasProperty("phone", parsedOptionalFields) && parsedOptionalFields["phone"] === "optional" ? " (optional)" : ""}`}
                                hasError={formError['phone']}
                                validationRule={validationModel.phone}
                                errorMessage={formError['phone']}
                                type={FORM_INPUT_TYPES.PHONE}
                                value={formValues?.phone ?? ""}
                            />
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col xs={24} className={showSameAsPropertyAddressCheckboxOption ? "" : "mb-12"}>
                            {
                                restrictAddressToCity ? (
                                    <CityRestrictedAddressLookup
                                        xs={24}
                                        name="mailingAddress"
                                        label={`Mailing Address${objectHasProperty("mailingAddress", parsedOptionalFields) && parsedOptionalFields["mailingAddress"] === "optional" ? " (optional)" : ""}`}
                                        hasError={formError['mailingAddress']}
                                        validationRule={validationModel.mailingAddress}
                                        errorMessage="Address is required"
                                        checkTrigger="none"
                                        onTextChanged={onAddressTextChanged}
                                    />

                                ) : (
                                    <UnrestrictedAddressLookup
                                        xs={24}
                                        name="mailingAddress"
                                        label={`Mailing Address${objectHasProperty("mailingAddress", parsedOptionalFields) && parsedOptionalFields["mailingAddress"] === "optional" ? " (optional)" : ""}`}
                                        hasError={formError['mailingAddress']}
                                        validationRule={validationModel.mailingAddress}
                                        errorMessage="Address is required"
                                        checkTrigger="none"
                                        onItemSelected={(item) => onUnrestrictedAddressLookupChange(item)}
                                        value={formValues?.mailingAddress ? titleize(formValues?.mailingAddress) : ""}
                                        onTextChanged={onAddressTextChanged}
                                    />
                                )
                            }
                        </Col>
                    </Row>
                    {
                        showSameAsPropertyAddressCheckboxOption && (
                            <Row className="mt-4" gutter={24}>
                                <Col xs={24} className="mb-12">
                                    <AnnotatedCheckbox 
                                        label="Same as property address" 
                                        name="sameAsPropertyAddress" 
                                        value={formValues?.sameAsPropertyAddress} 
                                        onChange={(v) => onSameAsPropertyAddressChanged(v)} 
                                    />
                                </Col>
                            </Row>
                        )
                    }
                </Grid>
            </Form>
        )
    }
)

export default PersonDetails
